import * as React from 'react';
import { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context } from '@micro-frontend-react/core/lib/Context';

import { PBTApiEndpoints, PBTMessages } from '../PBTConstants';

export type HrcValidationReleaseState = 'Idle' | 'Preparing' | 'Pending' | 'Processing' | 'Completed' | 'Failed';

interface IHrcValidationReleaseStatus {
  caseId: number;
  state: Exclude<HrcValidationReleaseState, 'Idle' | 'Preparing'>;
  error?: string | null;
}

interface IPmResponse<T> {
  data?: T;
  error?: { message?: string };
}

const POLL_INTERVAL_MS = 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

// While the HRC validation packet is still being assembled in the background, the gate release
// is deferred with HTTP 409. Rather than surfacing this as a re-clickable error, we show a
// transient "preparing" state and re-POST proceed automatically until the packet is ready.
// The build is Adobe-throttled (~25s) plus a background poll interval, so allow a generous
// budget (~40s) before giving up.
const PREPARING_RETRY_INTERVAL_MS = 2000;
const MAX_PREPARING_RETRIES = 20;
// The server message is the only discriminator between "packet preparing" and other 409s
// (e.g. NotInGate), so match a stable fragment of the backend message. If the backend wording
// changes this degrades gracefully to today's behavior (the message is shown as an error).
const PACKET_PREPARING_MARKER = 'being prepared';

export function useProceedHrcValidation(onSuccess?: () => void) {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const onSuccessRef = React.useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const pollGeneration = React.useRef(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [state, setState] = React.useState<HrcValidationReleaseState>('Idle');
  const [error, setError] = React.useState<string | null>(null);

  const pollStatus = React.useCallback(async (
    caseId: number,
    generation: number,
    consecutiveFailures = 0
  ): Promise<void> => {
    if (generation !== pollGeneration.current) return;

    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.HrcValidationProceedStatus(caseId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await response.json().catch(() => null) as IPmResponse<IHrcValidationReleaseStatus> | null;

      if (!response.ok || !body?.data) {
        throw new Error(body?.error?.message || PBTMessages.ProceedError);
      }
      if (generation !== pollGeneration.current) return;

      const status = body.data;
      setState(status.state);
      if (status.state === 'Completed') {
        setError(null);
        onSuccessRef.current?.();
        return;
      }
      if (status.state === 'Failed') {
        setError(status.error || PBTMessages.ProceedError);
        return;
      }

      window.setTimeout(() => void pollStatus(caseId, generation, 0), POLL_INTERVAL_MS);
    } catch (pollError) {
      if (generation !== pollGeneration.current) return;
      const nextFailureCount = consecutiveFailures + 1;
      if (nextFailureCount >= MAX_CONSECUTIVE_POLL_FAILURES) {
        setState('Failed');
        setError(pollError instanceof Error ? pollError.message : PBTMessages.ProceedError);
        return;
      }

      const retryDelay = Math.min(POLL_INTERVAL_MS * (2 ** consecutiveFailures), 10000);
      window.setTimeout(
        () => void pollStatus(caseId, generation, nextFailureCount),
        retryDelay
      );
    }
  }, [authClient]);

  const reset = React.useCallback(() => {
    pollGeneration.current += 1;
    setIsSubmitting(false);
    setState('Idle');
    setError(null);
  }, []);

  React.useEffect(() => () => {
    pollGeneration.current += 1;
  }, []);

  const submitProceed = React.useCallback(async (
    caseId: number,
    generation: number,
    preparingAttempt = 0
  ): Promise<boolean> => {
    if (generation !== pollGeneration.current) return false;

    try {
      const token = await authClient.acquireToken(__SCOPES__);
      if (generation !== pollGeneration.current) return false;

      const response = await fetch(PBTApiEndpoints.HrcValidationProceed(caseId), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const body = await response.json().catch(() => null) as IPmResponse<IHrcValidationReleaseStatus> | null;
      if (generation !== pollGeneration.current) return false;

      // Packet still being assembled: keep the UI in a transient "preparing" state and retry.
      if (
        response.status === 409 &&
        (body?.error?.message ?? '').toLowerCase().includes(PACKET_PREPARING_MARKER)
      ) {
        if (preparingAttempt >= MAX_PREPARING_RETRIES) {
          setState('Failed');
          setError(body?.error?.message || PBTMessages.ProceedError);
          return false;
        }
        setState('Preparing');
        setError(null);
        window.setTimeout(
          () => void submitProceed(caseId, generation, preparingAttempt + 1),
          PREPARING_RETRY_INTERVAL_MS
        );
        return true;
      }

      if (!response.ok || !body?.data) {
        // A non-preparing failure (or one after a preparing retry) is terminal: move out of any
        // transient 'Preparing' state so the error surfaces and the action becomes re-clickable.
        setState('Failed');
        setError(body?.error?.message || PBTMessages.ProceedError);
        return false;
      }

      setState(body.data.state);
      if (body.data.state === 'Completed') {
        onSuccessRef.current?.();
      } else if (body.data.state === 'Failed') {
        setError(body.data.error || PBTMessages.ProceedError);
      } else {
        void pollStatus(caseId, generation);
      }
      return true;
    } catch {
      if (generation !== pollGeneration.current) return false;
      setState('Failed');
      setError(PBTMessages.ProceedError);
      return false;
    }
  }, [authClient, pollStatus]);

  const proceed = React.useCallback(async (caseId: number) => {
    const generation = pollGeneration.current + 1;
    pollGeneration.current = generation;
    setIsSubmitting(true);
    setState('Idle');
    setError(null);

    try {
      return await submitProceed(caseId, generation, 0);
    } finally {
      if (generation === pollGeneration.current) {
        setIsSubmitting(false);
      }
    }
  }, [submitProceed]);

  const isProcessing = state === 'Preparing' || state === 'Pending' || state === 'Processing';
  return { proceed, isSubmitting, isProcessing, state, error, reset };
}
